import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Download, Eye, Paperclip, Pencil, Plus, Radio, RefreshCw, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sprekersApi } from '../api';
import { persistLiveSession } from '../liveSessionStorage';
import CanvasImportDialog from './CanvasImportDialog';
import PresentationSourceChooser from './PresentationSourceChooser';
import SlidePreviewDialog, { LazySlidePreview } from './SlidePreview';
import { getGoogleConfig } from '../google/googleConfig';
import useGoogleSlidesAccess from '../google/useGoogleSlidesAccess';
import { describeGoogleError, requestDriveFileToken } from '../google/googleSlides';
import SlideFormDialog from './SlideFormDialog';
import ConversionReportNotice from './ConversionReportNotice';
import useTemplates from '../hooks/useTemplates';
import {
  PRESENTATION_STATUSES, PRESENTATION_TYPES, SLIDE_TYPES, SLIDE_VISIBILITIES, findClassName, findLabel,
} from '../labels';
import type { CanvasBoardDetail, PresentationDetail, PresentationSlide } from '../types';

interface Props {
  engagementId: string;
  canEdit: boolean;
}

export default function PresentationTab({ engagementId, canEdit }: Props) {
  const navigate = useNavigate();
  const templates = useTemplates();
  const [presentation, setPresentation] = useState<PresentationDetail | null>(null);
  const [canvasBoard, setCanvasBoard] = useState<CanvasBoardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<PresentationSlide | null>(null);
  const [canvasImportOpen, setCanvasImportOpen] = useState(false);
  const [startingLive, setStartingLive] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const googleAllowed = useGoogleSlidesAccess();

  const fetchPresentation = async () => {
    try {
      const data = await sprekersApi.getPresentation(engagementId);
      setPresentation(data);
    } catch (error) {
      setPresentation(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPresentation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  useEffect(() => {
    if (!presentation || presentation.presentation_type !== 'canvas') {
      setCanvasBoard(null);
      return;
    }
    sprekersApi.getCanvasBoard(presentation.id).then(setCanvasBoard).catch(() => setCanvasBoard(null));
  }, [presentation]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResyncGoogle = async () => {
    const config = getGoogleConfig();
    if (!presentation || !config || !googleAllowed) return;
    if (!window.confirm(
      'Opnieuw synchroniseren vervangt alle slides van deze presentatie door de huidige versie uit Google Slides. '
      + 'Handmatige wijzigingen aan de slides (volgorde, extra slides, zichtbaarheid) gaan verloren. Doorgaan?',
    )) return;
    setResyncing(true);
    try {
      // Het access-token bestaat alleen in deze functie (nooit in state of storage).
      const accessToken = await requestDriveFileToken(config.clientId);
      setPresentation(await sprekersApi.resyncGoogleSlidesPresentation(presentation.id, accessToken));
      toast.success('Presentatie opnieuw gesynchroniseerd');
    } catch (error: any) {
      toast.error(describeGoogleError(error, 'Synchroniseren mislukt. De presentatie is ongewijzigd.'));
    } finally {
      setResyncing(false);
    }
  };

  const handleTemplateChange = async (templateId: string) => {
    if (!presentation) return;
    try {
      await sprekersApi.updatePresentation(presentation.id, { template_id: templateId });
      fetchPresentation();
    } catch (error) {
      toast.error('Bijwerken mislukt');
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!presentation) return;
    try {
      await sprekersApi.updatePresentation(presentation.id, { status: status as PresentationDetail['status'] });
      fetchPresentation();
    } catch (error) {
      toast.error('Bijwerken mislukt');
    }
  };

  const handleStartLive = async () => {
    if (!presentation) return;
    setStartingLive(true);
    try {
      const session = await sprekersApi.createSession(presentation.id);
      persistLiveSession(session);
      navigate(`/sprekers/sessies/${session.id}/presenteren`, { state: { session } });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Live sessie starten mislukt');
    } finally {
      setStartingLive(false);
    }
  };

  const startNewSlide = () => {
    setEditingSlide(null);
    setDialogOpen(true);
  };

  const startEditSlide = (slide: PresentationSlide) => {
    setEditingSlide(slide);
    setDialogOpen(true);
  };

  const handleSlideSubmit = async (data: Parameters<typeof sprekersApi.createSlide>[1]) => {
    if (!presentation) return;
    try {
      if (editingSlide) {
        await sprekersApi.updateSlide(editingSlide.id, data);
      } else {
        await sprekersApi.createSlide(presentation.id, data);
      }
      fetchPresentation();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
      throw error;
    }
  };

  const handleDeleteSlide = async (slideId: string) => {
    if (!window.confirm('Weet je zeker dat je deze slide wilt verwijderen?')) return;
    try {
      await sprekersApi.deleteSlide(slideId);
      fetchPresentation();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const moveSlide = async (index: number, direction: -1 | 1) => {
    if (!presentation) return;
    const slides = presentation.slides;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= slides.length) return;
    const a = slides[index];
    const b = slides[targetIndex];
    try {
      await Promise.all([
        sprekersApi.updateSlide(a.id, { position: b.position }),
        sprekersApi.updateSlide(b.id, { position: a.position }),
      ]);
      fetchPresentation();
    } catch (error) {
      toast.error('Volgorde wijzigen mislukt');
    }
  };

  const hasEditableSlides = !!presentation && presentation.slides.some((slide) => slide.slide_type === 'vrije_slide');
  const importReport = presentation?.source?.type === 'google_slides_editable' ? presentation.source.report : undefined;

  if (loading) return <p className="text-sm text-slate-500">Laden...</p>;

  if (!presentation) {
    if (!canEdit) return <p className="text-sm text-slate-500">Nog geen presentatie voor deze spreekbeurt.</p>;
    return <PresentationSourceChooser engagementId={engagementId} onCreated={setPresentation} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={findClassName(PRESENTATION_STATUSES, presentation.status)}>
            {findLabel(PRESENTATION_STATUSES, presentation.status)}
          </Badge>
          <Badge className={findClassName(PRESENTATION_TYPES, presentation.presentation_type)}>
            {findLabel(PRESENTATION_TYPES, presentation.presentation_type)}
          </Badge>
          {presentation.source?.type === 'pdf_import' && (
            <Badge className="bg-slate-100 text-slate-700">
              PDF-import: {presentation.source.filename} ({presentation.source.page_count} pagina&apos;s)
            </Badge>
          )}
          {presentation.source?.type === 'google_slides' && (
            <Badge className="bg-slate-100 text-slate-700">
              Google Slides: {presentation.source.name} ({presentation.source.page_count} pagina&apos;s)
            </Badge>
          )}
          {presentation.source?.type === 'google_slides_editable' && (
            <Badge className="bg-teal-100 text-teal-700">
              Google Slides (bewerkbaar): {presentation.source.name} ({presentation.source.page_count} pagina&apos;s)
            </Badge>
          )}
          {presentation.source?.type === 'template' && (
            <Badge className="bg-slate-100 text-slate-700">
              Sjabloon: {presentation.source.template_name} ({presentation.source.page_count} pagina&apos;s)
            </Badge>
          )}
          {presentation.presentation_type === 'slides' && presentation.slides.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setPreviewIndex(0)}>
              <Eye className="mr-2 h-4 w-4" />
              Voorbeeld bekijken
            </Button>
          )}
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {presentation.presentation_type === 'slides' && (
              <Select value={presentation.template_id || undefined} onValueChange={handleTemplateChange}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Sjabloon kiezen" /></SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={presentation.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESENTATION_STATUSES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {presentation.source?.type === 'google_slides' && googleAllowed && (
              <Button variant="outline" onClick={handleResyncGoogle} disabled={resyncing}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {resyncing ? 'Bezig met synchroniseren...' : 'Opnieuw synchroniseren'}
              </Button>
            )}
            {hasEditableSlides && (
              <Button variant="outline" onClick={() => navigate(`/sprekers/${engagementId}/studio`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Bewerken in Zoea Studio
              </Button>
            )}
            {presentation.presentation_type === 'canvas' && (
              <Button variant="outline" onClick={() => navigate(`/sprekers/${engagementId}/canvas-editor`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Canvas bewerken
              </Button>
            )}
            {presentation.presentation_type === 'canvas' && (
              <Button variant="outline" onClick={() => setCanvasImportOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                {canvasBoard ? 'Opnieuw importeren' : 'JSON importeren'}
              </Button>
            )}
            {(presentation.presentation_type === 'canvas' ? canvasBoard !== null : presentation.slides.length > 0) && (
              <Button onClick={handleStartLive} disabled={startingLive}>
                <Radio className="mr-2 h-4 w-4" />
                Start live modus
              </Button>
            )}
          </div>
        )}
      </div>

      {importReport && importReport.items.length > 0 && <ConversionReportNotice report={importReport} />}

      {presentation.presentation_type === 'canvas' ? (
        <div className="space-y-2">
          {canvasBoard ? (
            <p className="text-sm text-slate-500">
              {canvasBoard.frames.length} frames, pad van {canvasBoard.path.length} stappen
            </p>
          ) : (
            <p className="text-sm text-slate-500">Nog geen canvas geïmporteerd.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {presentation.slides.length === 0 && <p className="text-sm text-slate-500">Nog geen slides.</p>}
          {presentation.slides.map((slide, index) => (
            <div key={slide.id} className="flex items-start gap-3 rounded-lg border border-slate-100 p-3">
              <button
                type="button" onClick={() => setPreviewIndex(index)} aria-label={`Voorbeeld van slide ${index + 1}`}
                className="w-28 shrink-0 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 sm:w-40"
              >
                <LazySlidePreview slide={slide} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-slate-900">{index + 1}. {findLabel(SLIDE_TYPES, slide.slide_type)}</p>
                  <Badge className={findClassName(SLIDE_VISIBILITIES, slide.visibility)}>
                    {findLabel(SLIDE_VISIBILITIES, slide.visibility)}
                  </Badge>
                  {slide.media_id && <Paperclip className="h-3.5 w-3.5 text-slate-400" aria-label="Heeft media" />}
                </div>
                {slide.bible_reference && <p className="mt-1 text-sm text-slate-600">{slide.bible_reference}</p>}
                {slide.content && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-500">{slide.content}</p>}
                {slide.media_id && (
                  <Button
                    type="button" variant="link" size="sm" className="mt-1 h-auto p-0 text-xs"
                    onClick={() => sprekersApi.downloadMedia(slide.media_id as string, 'media')}
                  >
                    <Download className="mr-1 h-3 w-3" />
                    Media downloaden
                  </Button>
                )}
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="icon" onClick={() => moveSlide(index, -1)} disabled={index === 0} aria-label="Omhoog">
                    <ArrowUp className="h-4 w-4 text-slate-400" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" onClick={() => moveSlide(index, 1)}
                    disabled={index === presentation.slides.length - 1} aria-label="Omlaag"
                  >
                    <ArrowDown className="h-4 w-4 text-slate-400" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" aria-label="Bewerken"
                    onClick={() => (slide.slide_type === 'vrije_slide'
                      ? navigate(`/sprekers/${engagementId}/studio?slide=${slide.id}`)
                      : startEditSlide(slide))}
                  >
                    <Pencil className="h-4 w-4 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteSlide(slide.id)} aria-label="Verwijderen">
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                </div>
              )}
            </div>
          ))}

          {canEdit && (
            <Button variant="outline" onClick={startNewSlide}>
              <Plus className="mr-2 h-4 w-4" />
              Slide toevoegen
            </Button>
          )}
        </div>
      )}

      <SlidePreviewDialog slides={presentation.slides} index={previewIndex} onIndexChange={setPreviewIndex} />
      <SlideFormDialog
        open={dialogOpen} onOpenChange={setDialogOpen} slide={editingSlide}
        engagementId={engagementId} onSubmit={handleSlideSubmit}
      />
      <CanvasImportDialog
        open={canvasImportOpen} onOpenChange={setCanvasImportOpen}
        presentationId={presentation.id} onImported={setCanvasBoard}
      />
    </div>
  );
}
